import { validateHSCodesWithHMRC } from '../../api/hmrcValidator'
import { fetchJobVerificationData, resolveJobBundle } from './fetchVerificationData'
import type { CIInvoice, CIVerificationData } from './ciFromUpload'
import { addressTokenString, comparePartyRow } from './normalize'
import type { AddressFacets } from './normalize'
import type { CheckDetailRow, CheckResult, TotalsCheckResult, UploadedCIMeta, VerificationResult } from './types'

const GROSS_WEIGHT_EPSILON = 0.001 // float-safety only (DECIMAL rounding), not a business tolerance

function naResult(id: CheckResult['id'], title: string, note: string): CheckResult {
  return { id, title, status: 'na', summary: note, rows: [] }
}

// ciData/ciMeta come from a staff-uploaded file (see ciFromUpload.ts) — this
// no longer queries commercial_invoices/invoice_items at all.
export async function runVerification(
  jobRef: string,
  ciData: CIVerificationData,
  ciMeta: UploadedCIMeta,
  verifiedBy: string
): Promise<VerificationResult> {
  const bundle = await resolveJobBundle(jobRef)
  const consol = bundle.isConsolBundle
    ? { parentJobRef: bundle.primaryJob.job_ref, childJobRefs: bundle.childJobRefs }
    : null

  const jobData = await fetchJobVerificationData(bundle)

  const noInvoicesFound = ciData.invoices.length === 0

  if (noInvoicesFound) {
    const note = `No commercial invoice data available for job_ref "${jobRef}"`
    const checks: VerificationResult['checks'] = [
      naResult('shipper_name', 'Shipper Name Match', note),
      naResult('shipper_address', 'Shipper Address Match', note),
      naResult('importer_name', 'Importer Name Match', note),
      naResult('importer_address', 'Importer Address Match', note),
      naResult('incoterm', 'Incoterm Match', note),
      naResult('commodities', 'Commodities', note),
      {
        id: 'totals', title: 'Totals', status: 'fail' as const, summary: note,
        packages: { jobValue: jobData.totals.packages, ciValue: 0, status: 'fail' },
        grossWeight: { jobValue: jobData.totals.grossWeight, ciValue: 0, status: 'fail' },
        cbm: { jobValue: jobData.totals.cbm, ciValue: null, status: 'na', note: 'No commercial invoice uploaded' },
      } as TotalsCheckResult,
      naResult('hs_codes', 'HS Code Validity', note),
    ]
    return {
      jobRef,
      verifiedAt: new Date().toISOString(),
      verifiedBy,
      cisIncluded: [],
      noInvoicesFound: true,
      checks,
      // Computed rather than hardcoded (this branch previously hardcoded
      // failCount: 0/naCount: 6, which was already wrong even before the
      // split -- totals above is 'fail', not 'na', so it was 5 na + 1 fail,
      // not 6 na) -- same pattern the normal path below already uses.
      passCount: checks.filter(c => c.status === 'pass').length,
      failCount: checks.filter(c => c.status === 'fail').length,
      naCount: checks.filter(c => c.status === 'na').length,
      consol,
    }
  }

  const [shipperNameCheck, shipperAddressCheck] = runPartyChecks(
    'shipper', jobData.consignor, ciData.invoices, ci => ci.shipper, ci => ci.shipper_address, 'shipper',
  )
  const [importerNameCheck, importerAddressCheck] = runPartyChecks(
    'importer', jobData.importer, ciData.invoices, ci => ci.consignee, ci => ci.consignee_address, 'consignee',
  )
  const incotermCheck = runIncotermCheck(jobData, ciData)
  const commoditiesCheck = runCommoditiesCheck(jobData, ciData)
  const totalsCheck = runTotalsCheck(jobData, ciData)
  const hsCodesCheck = await runHsCodesCheck(ciData)

  const checks: VerificationResult['checks'] = [
    shipperNameCheck, shipperAddressCheck, importerNameCheck, importerAddressCheck,
    incotermCheck, commoditiesCheck, totalsCheck, hsCodesCheck,
  ]

  const passCount = checks.filter(c => c.status === 'pass').length
  const failCount = checks.filter(c => c.status === 'fail').length
  const naCount = checks.filter(c => c.status === 'na').length

  return {
    jobRef,
    verifiedAt: new Date().toISOString(),
    verifiedBy,
    cisIncluded: [ciMeta],
    noInvoicesFound: false,
    checks,
    passCount,
    failCount,
    naCount,
    consol,
  }
}

// Task T: replaces runShipperCheck/runImporterCheck, which each returned one
// combined CheckResult (name+address rolled into a single pass/fail, via
// comparePartyRow's `status`). Now returns 2 independent CheckResults so
// staff see -- and can override -- name and address results separately.
// comparePartyRow itself is unchanged; this just stops rolling its
// nameStatus/addressStatus into one combined row/check.
export function runPartyChecks(
  party: 'shipper' | 'importer',
  jobParty: AddressFacets | null,
  invoices: CIInvoice[],
  getCiName: (ci: CIInvoice) => string | null,
  getCiAddress: (ci: CIInvoice) => string | null,
  fieldLabel: string,
): [CheckResult, CheckResult] {
  const nameId = `${party}_name` as const
  const addressId = `${party}_address` as const
  const partyTitle = party === 'shipper' ? 'Shipper' : 'Importer'
  const nameTitle = `${partyTitle} Name Match`
  const addressTitle = `${partyTitle} Address Match`

  if (!jobParty) {
    const note = `Job has no ${party === 'shipper' ? 'consignor' : 'importer'} on file`
    return [naResult(nameId, nameTitle, note), naResult(addressId, addressTitle, note)]
  }

  const addressTokens = addressTokenString(jobParty)
  const comparisons = invoices.map(ci => ({ ci, cmp: comparePartyRow(jobParty.name, addressTokens, getCiName(ci), getCiAddress(ci), fieldLabel) }))

  const nameRows: CheckDetailRow[] = comparisons.map(({ ci, cmp }) => ({
    label: ci.invoice_no ?? ci.id,
    jobValue: jobParty.name,
    ciValue: getCiName(ci),
    status: cmp.nameStatus,
    note: cmp.nameNote,
  }))
  const addressRows: CheckDetailRow[] = comparisons.map(({ ci, cmp }) => ({
    label: ci.invoice_no ?? ci.id,
    jobValue: addressTokens || null,
    ciValue: getCiAddress(ci),
    status: cmp.addressStatus,
    note: cmp.addressNote,
  }))

  const nameCheck: CheckResult = {
    id: nameId,
    title: nameTitle,
    status: nameRows.every(r => r.status === 'match') ? 'pass' : 'fail',
    summary: `${nameRows.filter(r => r.status === 'match').length} of ${nameRows.length} CIs match`,
    rows: nameRows,
  }

  // Address (unlike name) can genuinely have no data captured on either side
  // for every CI -- that's 'na' (not a failure), distinct from a real
  // mismatch, so staff see a dedicated N/A badge rather than a false fail.
  const addressCheck: CheckResult = {
    id: addressId,
    title: addressTitle,
    status: addressRows.some(r => r.status === 'mismatch') ? 'fail' : addressRows.every(r => r.status === 'na') ? 'na' : 'pass',
    summary: addressRows.every(r => r.status === 'na')
      ? 'No address data captured on the Job or CI side for any CI'
      : `${addressRows.filter(r => r.status === 'match').length} of ${addressRows.length} CIs match`,
    rows: addressRows,
  }

  return [nameCheck, addressCheck]
}

function runIncotermCheck(jobData: Awaited<ReturnType<typeof fetchJobVerificationData>>, ciData: CIVerificationData): CheckResult {
  const jobCode = jobData.shippingTermsCode
  if (!jobCode) {
    return naResult('incoterm', 'Incoterm Match', 'No shipping terms set on this job')
  }
  const normalizedJobCode = jobCode.trim().toUpperCase()
  const rows: CheckDetailRow[] = ciData.invoices.map(ci => {
    const ciCode = (ci.incoterms ?? '').trim().toUpperCase()
    const matched = ciCode === normalizedJobCode
    return {
      label: ci.invoice_no ?? ci.id,
      jobValue: normalizedJobCode,
      ciValue: ci.incoterms,
      status: matched ? 'match' : 'mismatch',
    }
  })
  const allMatch = rows.every(r => r.status === 'match')
  return {
    id: 'incoterm',
    title: 'Incoterm Match',
    status: allMatch ? 'pass' : 'fail',
    summary: `${rows.filter(r => r.status === 'match').length} of ${rows.length} CIs match`,
    rows,
  }
}

function runCommoditiesCheck(jobData: Awaited<ReturnType<typeof fetchJobVerificationData>>, ciData: CIVerificationData): CheckResult {
  const jobCommodities = jobData.jobGoods.map(g => g.goods_description).filter((d): d is string => !!d && d.trim().length > 0)
  const ciDescriptions = ciData.items.map(i => i.goods_description).filter((d): d is string => !!d)

  if (jobCommodities.length === 0) {
    return naResult('commodities', 'Commodities', 'Job has no commodity descriptions on file')
  }

  const rows: CheckDetailRow[] = jobCommodities.map(commodity => {
    const found = ciDescriptions.some(d => d.toLowerCase().includes(commodity.toLowerCase()))
    return {
      label: commodity,
      jobValue: commodity,
      ciValue: found ? commodity : null,
      status: found ? 'match' : 'mismatch',
      note: found ? undefined : 'not found in any CI item description',
    }
  })
  const allMatch = rows.every(r => r.status === 'match')
  return {
    id: 'commodities',
    title: 'Commodities',
    status: allMatch ? 'pass' : 'fail',
    summary: `${rows.filter(r => r.status === 'match').length} of ${rows.length} Job commodities found in CI items`,
    rows,
  }
}

function runTotalsCheck(jobData: Awaited<ReturnType<typeof fetchJobVerificationData>>, ciData: CIVerificationData): TotalsCheckResult {
  const jobPackages = jobData.totals.packages
  const ciPackages = ciData.totals.packages
  const packagesMatch = jobPackages === ciPackages

  const jobGrossWeight = jobData.totals.grossWeight
  const ciGrossWeight = ciData.totals.grossWeightKg
  const grossWeightMatch = Math.abs(jobGrossWeight - ciGrossWeight) < GROSS_WEIGHT_EPSILON

  // K10: CBM is strict pass/fail like packages/grossWeight once both sides have a
  // captured figure -- but falls back to 'na' (not a fail) when either side has none,
  // since a missing CBM column/volume figure isn't the same as a genuine mismatch.
  const jobCbm = jobData.totals.cbm
  const ciCbm = ciData.totals.cbm
  const cbmCaptured = jobCbm != null && ciCbm != null
  const cbmMatch = cbmCaptured && Math.abs(jobCbm - ciCbm) < GROSS_WEIGHT_EPSILON

  const status = packagesMatch && grossWeightMatch && (!cbmCaptured || cbmMatch) ? 'pass' : 'fail'
  const diffNotes: string[] = []
  if (!packagesMatch) {
    const diff = jobPackages - ciPackages
    diffNotes.push(diff > 0 ? `CI ${diff} fewer package(s) than Job` : `CI ${-diff} more package(s) than Job`)
  }
  if (!grossWeightMatch) {
    const diff = jobGrossWeight - ciGrossWeight
    diffNotes.push(diff > 0 ? `CI ${diff.toFixed(3)}kg lighter than Job` : `CI ${(-diff).toFixed(3)}kg heavier than Job`)
  }
  if (cbmCaptured && !cbmMatch) {
    const diff = jobCbm! - ciCbm!
    diffNotes.push(diff > 0 ? `CI ${diff.toFixed(3)}m³ less than Job` : `CI ${(-diff).toFixed(3)}m³ more than Job`)
  }

  return {
    id: 'totals',
    title: 'Totals',
    status,
    summary: status === 'pass' ? 'Packages, gross weight' + (cbmCaptured ? ', and CBM match' : ' match') : diffNotes.join('; '),
    packages: { jobValue: jobPackages, ciValue: ciPackages, status: packagesMatch ? 'pass' : 'fail' },
    grossWeight: { jobValue: jobGrossWeight, ciValue: ciGrossWeight, status: grossWeightMatch ? 'pass' : 'fail' },
    cbm: cbmCaptured
      ? { jobValue: jobCbm, ciValue: ciCbm, status: cbmMatch ? 'pass' : 'fail' }
      : { jobValue: jobCbm, ciValue: ciCbm, status: 'na', note: jobCbm == null ? 'Job has no CBM/volume recorded' : 'Not captured on this commercial invoice' },
  }
}

async function runHsCodesCheck(ciData: CIVerificationData): Promise<CheckResult> {
  const codes = Array.from(new Set(ciData.items.map(i => i.hs_code).filter((c): c is string => !!c)))

  if (codes.length === 0) {
    return { id: 'hs_codes', title: 'HS Code Validity', status: 'pass', summary: 'No HS codes to validate', rows: [] }
  }

  const validations = await validateHSCodesWithHMRC(codes)
  const invalidCodes = new Set(validations.filter(v => !v.valid).map(v => v.hs_code))

  if (invalidCodes.size === 0) {
    return { id: 'hs_codes', title: 'HS Code Validity', status: 'pass', summary: `All ${codes.length} HS codes valid`, rows: [] }
  }

  const invoicesById = new Map(ciData.invoices.map(i => [i.id, i]))
  const rows: CheckDetailRow[] = ciData.items
    .filter(item => item.hs_code && invalidCodes.has(item.hs_code))
    .map(item => {
      const invoice = invoicesById.get(item.invoice_id)
      return {
        label: `${invoice?.invoice_no ?? item.invoice_id} / line ${item.line_no ?? '?'}`,
        jobValue: null,
        ciValue: item.hs_code,
        status: 'mismatch',
        note: validations.find(v => v.hs_code === item.hs_code)?.error ?? 'invalid HS code',
      }
    })

  return {
    id: 'hs_codes',
    title: 'HS Code Validity',
    status: 'fail',
    summary: `${invalidCodes.size} of ${codes.length} HS codes invalid`,
    rows,
  }
}
